/** DailyChallengeStore — 毎日のチャレンジステージと自己ベストを永続化。
 *  Reference: Canvas Kingdom Phase A — Daily Challenge MVP
 *  - 当日のステージ ID は日付シードで全 stage_ids から決定(全プレイヤー同条件)
 *  - 自己ベスト(coverage %)を localStorage に保存
 *  - 毎日 0:00 リセット(日付が変わったら新規生成)
 *  - Pay-to-Win 禁止: スキップ・課金優位なし(Skill Honored 準拠)
 */

export interface DailyChallengeState {
  readonly date: string;           // 'YYYY-MM-DD'
  readonly stageId: string;        // 当日のチャレンジステージ
  readonly bestCoverage: number;   // 0..1
  readonly attempts: number;
}

const STORAGE_KEY = 'ck.dailyChallenge';

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** 日付文字列から決定論的なハッシュ値を生成する(全プレイヤー同一ステージ保証) */
function dateHash(date: string): number {
  let hash = 0;
  for (let i = 0; i < date.length; i++) {
    hash += date.charCodeAt(i);
  }
  return hash;
}

export class DailyChallengeStore {
  private readonly allStageIds: readonly string[];
  private state: DailyChallengeState;

  constructor(allStageIds: readonly string[]) {
    this.allStageIds = allStageIds;
    this.state = this.loadOrRefresh();
  }

  private loadOrRefresh(): DailyChallengeState {
    const today = todayStr();
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<DailyChallengeState>;
        if (
          parsed.date === today &&
          typeof parsed.stageId === 'string' &&
          typeof parsed.bestCoverage === 'number' &&
          typeof parsed.attempts === 'number'
        ) {
          return {
            date: today,
            stageId: parsed.stageId,
            bestCoverage: Math.max(0, Math.min(1, parsed.bestCoverage)),
            attempts: Math.max(0, parsed.attempts),
          };
        }
      }
    } catch {
      /* fall through to generate */
    }
    return this.freshState(today);
  }

  private freshState(date: string): DailyChallengeState {
    if (this.allStageIds.length === 0) {
      return { date, stageId: '', bestCoverage: 0, attempts: 0 };
    }
    const hash = dateHash(date);
    const stageId = this.allStageIds[((hash % this.allStageIds.length) + this.allStageIds.length) % this.allStageIds.length]!;
    return { date, stageId, bestCoverage: 0, attempts: 0 };
  }

  private save(): void {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch {
      /* noop(プライベートモード等) */
    }
  }

  /** 今日のチャレンジ情報を取得(必要なら新規生成) */
  getToday(): DailyChallengeState {
    const today = todayStr();
    if (this.state.date !== today) {
      this.state = this.freshState(today);
      this.save();
    }
    return this.state;
  }

  /** クリア/失敗時に自己ベストを更新 */
  recordAttempt(coverage: number): void {
    const today = todayStr();
    if (this.state.date !== today) {
      this.state = this.freshState(today);
    }
    this.state = {
      date: this.state.date,
      stageId: this.state.stageId,
      bestCoverage: Math.max(this.state.bestCoverage, Math.max(0, Math.min(1, coverage))),
      attempts: this.state.attempts + 1,
    };
    this.save();
  }

  /** デバッグ・テスト用: ステートをリセット */
  _reset(): void {
    this.state = this.freshState(todayStr());
    this.save();
  }
}
